import React from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getControlTokens } from './controlTokens';

function ControlButton({
  context = 'filterBar',
  variant = 'text',
  buttonVariant,
  selected = false,
  icon,
  children,
  tooltip,
  ariaLabel,
  onClick,
  disabled = false,
  sx,
  ...props
}) {
  const theme = useTheme();
  const tokens = getControlTokens(theme, context);
  const label = ariaLabel || (typeof children === 'string' ? children : undefined);
  const muiVariant = buttonVariant || 'outlined';
  const usesTokenSurface = muiVariant !== 'contained';

  if (variant === 'icon') {
    const button = (
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        aria-pressed={selected || undefined}
        sx={{
          width: tokens.height,
          height: tokens.height,
          minWidth: tokens.height,
          minHeight: tokens.height,
          borderRadius: `${tokens.radius}px`,
          border: `1px solid ${selected ? tokens.selectedColor : tokens.borderColor}`,
          backgroundColor: selected ? tokens.selected : tokens.surface,
          color: selected ? tokens.selectedColor : tokens.textColor,
          '&:hover': {
            backgroundColor: selected ? tokens.selectedHover : tokens.hover,
          },
          '& .MuiSvgIcon-root': {
            fontSize: tokens.iconSize,
          },
          ...sx,
        }}
        {...props}
      >
        {icon || children}
      </IconButton>
    );
    return tooltip ? (
      <Tooltip title={tooltip}>
        {disabled ? <span>{button}</span> : button}
      </Tooltip>
    ) : button;
  }

  const button = (
    <Button
      size="small"
      variant={muiVariant}
      startIcon={variant === 'iconText' ? icon : undefined}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={selected || undefined}
      sx={{
        minHeight: tokens.height,
        height: tokens.height,
        px: tokens.buttonPx,
        borderRadius: `${tokens.radius}px`,
        textTransform: 'none',
        fontSize: tokens.fontSize,
        fontWeight: 650,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        color: usesTokenSurface
          ? (selected ? tokens.selectedColor : tokens.textColor)
          : undefined,
        borderColor: usesTokenSurface
          ? (selected ? tokens.selectedColor : tokens.borderColor)
          : undefined,
        backgroundColor: usesTokenSurface
          ? (selected ? tokens.selected : tokens.surface)
          : undefined,
        '&:hover': usesTokenSurface ? {
          borderColor: selected ? tokens.selectedColor : tokens.borderColor,
          backgroundColor: selected ? tokens.selectedHover : tokens.hover,
        } : undefined,
        '& .MuiButton-startIcon': {
          mr: tokens.iconLabelGap,
          ml: 0,
          '& .MuiSvgIcon-root': {
            fontSize: tokens.iconSize,
          },
        },
        ...sx,
      }}
      {...props}
    >
      {children}
    </Button>
  );

  return tooltip ? (
    <Tooltip title={tooltip}>
      {disabled ? <span>{button}</span> : button}
    </Tooltip>
  ) : button;
}

export default ControlButton;
